// src/components/TopHeader.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaSignOutAlt, FaComments } from 'react-icons/fa';
import { useChat } from '../contexts/ChatContext.jsx';
import './TopHeader.css';

const TopHeader = () => {
  const API = import.meta.env.VITE_API_URL;
  const navigate = useNavigate();
  const [pageTitle, setPageTitle] = useState('');
  const [pageSubtitle, setPageSubtitle] = useState('');
  const [pageStatus, setPageStatus] = useState('');
  const { setIsDmOpen, unreadCount } = useChat();

  useEffect(() => {
    const onSetTitle = (e) => {
      const { title = '', subtitle = '' } = e?.detail || {};
      setPageTitle(String(title || ''));
      setPageSubtitle(String(subtitle || ''));
    };
    const onClearTitle = () => {
      setPageTitle('');
      setPageSubtitle('');
    };
    const onSetStatus = (e) => {
      const { status = '' } = e?.detail || {};
      setPageStatus(String(status || ''));
    };
    const onClearStatus = () => setPageStatus('');
    window.addEventListener('topbar:setTitle', onSetTitle);
    window.addEventListener('topbar:clearTitle', onClearTitle);
    window.addEventListener('topbar:setStatus', onSetStatus);
    window.addEventListener('topbar:clearStatus', onClearStatus);
    return () => {
      window.removeEventListener('topbar:setTitle', onSetTitle);
      window.removeEventListener('topbar:clearTitle', onClearTitle);
      window.removeEventListener('topbar:setStatus', onSetStatus);
      window.removeEventListener('topbar:clearStatus', onClearStatus);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/logout`, { method: 'POST' });
    } catch {}
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('authToken');
      // Clear chat-related UI flags
      localStorage.removeItem('chat.agent.open');
      localStorage.removeItem('chat.dm.open');
      localStorage.removeItem('chat.dm.activeUserId');
    } catch {}
    try { window.dispatchEvent(new CustomEvent('auth:logout')); } catch {}
    navigate('/');
  };

  const user = (() => {
    try {
      // Prefer currentUser if present (often richer), else fallback to user
      const cu = localStorage.getItem('currentUser');
      const u  = localStorage.getItem('user');
      return cu ? JSON.parse(cu) : (u ? JSON.parse(u) : {});
    } catch { return {}; }
  })();

  const isClientUser = (u) => {
    const v = u?.isclient ?? u?.isClient ?? u?.role ?? u?.roleid;
    if (v === true) return true;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
    }
    // Heuristic: presence of a client id implies client user in this app
    const cid = u?.clientid ?? u?.clientId ?? u?.clientID ?? null;
    return Number.isFinite(Number(cid)) && Number(cid) > 0;
  };
  const cid = user?.clientid ?? user?.clientId ?? user?.clientID ?? null;
  const IS_CLIENT = isClientUser(user);

  const handleChatOpen = () => {
    try { setIsDmOpen(true); } catch { /* noop */ }
  };

  const displayName = (user && (user.fullname || user.name || user.login)) || '';

  return (
    <header className="top-header">
      <div className="topbar-left">
        {!!pageTitle && (
          <h1 className="topbar-title">
            {pageTitle}
            {!!pageSubtitle && <span className="topbar-subtitle">{pageSubtitle}</span>}
          </h1>
        )}
      </div>
      <div className="topbar-center">
        {!!pageStatus && (
          <div className="topbar-status" title={pageStatus}>{pageStatus}</div>
        )}
      </div>
      <div className="top-header-user">
        {displayName ? (
          <>
            <span className="user-greeting">Welcome, {user.fullname || user.name || user.login || clientDisplayName}</span>
            <button
              className="icon-button"
              onClick={() => navigate('/settings')}
              title="Settings"
              aria-label="Settings"
            >
              <FaCog />
            </button>

            {!IS_CLIENT && (
              <button className="icon-button chat-with-badge" onClick={handleChatOpen} title="Chat" aria-label="Chat">
                <FaComments />
                {unreadCount > 0 && (
                  <span className="chat-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>
            )}
            
            <button
              className="icon-button"
              onClick={handleLogout}
              title="Logout"
              aria-label="Logout"
            >
              <FaSignOutAlt />
            </button>
          </>
        ) : (
          <span className="user-greeting">Not logged in</span>
        )}
      </div>
    </header>
  );
};

export default TopHeader;
